import { useCallback, useMemo, useState } from 'react';
import { Button, ButtonSizes, ButtonVariants, Dropdown, Table } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { buildTableLabels } from '@/config/tableLabels';
import { useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import {
  useGetAccountMbPeriodsQuery,
  useGetAccountTerminalsQuery,
  useGetSiteDetailQuery,
  useGetSiteMbPeriodQuery,
} from '../../api/sitesApi';
import { buildSiteMbPeriodColumns } from '../../constants/siteMbPeriodColumns';
import { MassBalancePeriodModal } from './MassBalancePeriodModal';
import styles from './MassBalancePeriodSection.module.scss';

const getMbPeriodRowId = (row) => row.id;

export const MassBalancePeriodSection = () => {
  const t = useTranslations();
  const [selectedTerminalId, setSelectedTerminalId] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: terminals } = useGetAccountTerminalsQuery();
  const terminalOptions = useMemo(
    () =>
      (terminals ?? EMPTY_LIST).map((terminal) => ({ value: terminal.id, label: terminal.label })),
    [terminals],
  );

  const terminalId = selectedTerminalId || (terminals?.[0]?.id ?? '');
  const scope = useMemo(
    () => (terminals ?? EMPTY_LIST).find((terminal) => terminal.id === terminalId),
    [terminals, terminalId],
  );

  const { data: site } = useGetSiteDetailQuery(scope?.siteId, { skip: !scope });
  const { data, isFetching, isError, refetch } = useGetSiteMbPeriodQuery(
    { siteId: scope?.siteId, terminalId },
    { skip: !scope },
  );

  const { data: accountMbPeriods } = useGetAccountMbPeriodsQuery();
  const hasNoMbPeriods = accountMbPeriods?.isEmpty === true;

  const rows = data?.rows ?? EMPTY_LIST;
  const hasPeriods = rows.length > 0;

  const handleTerminalChange = useCallback(
    (event) => setSelectedTerminalId(event.target.value),
    [],
  );
  const handleRetry = useCallback(() => refetch(), [refetch]);
  const handleOpenModal = useCallback(() => setIsModalOpen(true), []);
  const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

  const columns = useMemo(() => buildSiteMbPeriodColumns(t), [t]);
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const emptyState = useMemo(() => {
    if (isError) {
      return {
        title: t('SITES_MB_PERIOD_LOAD_ERROR'),
        primaryLabel: t('COMMON_RETRY'),
        onPrimaryClick: handleRetry,
      };
    }

    return {
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('SITES_MB_PERIOD_EMPTY_TITLE'),
      description:
        scope && !hasNoMbPeriods
          ? t('SITES_MB_PERIOD_EMPTY_DESCRIPTION_SCOPED', { scope: scope.label })
          : t('SITES_MB_PERIOD_EMPTY_DESCRIPTION'),
      primaryLabel: t('SITES_MB_PERIOD_ACTION_UPDATE'),
      primaryIcon: <Plus />,
      onPrimaryClick: scope ? handleOpenModal : undefined,
    };
  }, [t, isError, scope, hasNoMbPeriods, handleRetry, handleOpenModal]);

  return (
    <div className={styles.card}>
      {!hasNoMbPeriods && (
        <div className={styles.toolbar}>
          <Dropdown
            className={styles.scopeSelect}
            value={terminalId}
            onChange={handleTerminalChange}
            options={terminalOptions}
            placeholder={t('FORM_SELECT_TERMINAL')}
            emptyMessage={t('FORM_TERMINAL_EMPTY')}
            aria-label={t('SITES_MB_PERIOD_SCOPE')}
          />

          <div className={styles.actions}>
            {hasPeriods && (
              <Button
                variant={ButtonVariants.secondary}
                size={ButtonSizes.sm}
                onClick={handleOpenModal}
              >
                {t('SITES_MB_PERIOD_ACTION_EDIT_SELECTED')}
              </Button>
            )}

            <Button
              variant={ButtonVariants.primary}
              size={ButtonSizes.sm}
              disabled={!scope}
              onClick={handleOpenModal}
            >
              {t('SITES_MB_PERIOD_ACTION_UPDATE')}
            </Button>
          </div>
        </div>
      )}

      <Table
        columns={columns}
        data={rows}
        getRowId={getMbPeriodRowId}
        loading={isFetching}
        emptyState={emptyState}
        labels={tableLabels}
      />

      {site && (
        <MassBalancePeriodModal
          open={isModalOpen}
          onClose={handleCloseModal}
          site={site}
          terminalId={terminalId}
          mbPeriod={data}
        />
      )}
    </div>
  );
};
